import { useMemo } from "react";

export const HEADCOUNT_PERMISSION_TYPES = new Set([
  "COMPANY_EMPLOYEE",
  "COMPANY_MANAGER",
  "COMPANY_ADMIN",
  "COMPANY_TIMECLOCK",
  "RIGHTWORK_INTERNAL",
]);

export function isActiveEmployee(member) {
  if (!member) {
    return false;
  }
  const status = String(member.status || "").toLowerCase();
  if (status === "archived" || status === "hidden") {
    return false;
  }
  if (member.is_hidden) {
    return false;
  }
  if (!member.permission_type) {
    return true;
  }
  const normalizedPermission = String(member.permission_type).toUpperCase();
  if (HEADCOUNT_PERMISSION_TYPES.has(normalizedPermission)) {
    return true;
  }
  // If the permission type is unrecognized, default to including the employee so
  // the roster view can still surface their data for headcount planning.
  return true;
}

function normalizeRoleTypeString(value) {
  if (typeof value !== "string") {
    return value;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return trimmed;
  }
  const commaParts = trimmed
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (commaParts.length > 1) {
    const lastPart = commaParts[commaParts.length - 1];
    if (/^\d+$/.test(lastPart)) {
      return commaParts.slice(0, -1).join(", ");
    }
  }
  return trimmed;
}

export function getMemberRoleTypes(member, roleTypeNameMap) {
  if (!member) {
    return [];
  }
  const roleTypes = new Set();
  const register = (value) => {
    const resolved = registerRoleTypeName(value, roleTypeNameMap);
    if (resolved) {
      roleTypes.add(resolved);
    }
  };

  register(member.primary_role_type_name);
  register(member.primary_role_type);

  member.roles_and_role_types?.forEach((assignment) => {
    register(assignment?.role_type);
    register(assignment?.role_type_name);
    register(assignment?.role_type_obj);
    register(assignment?.scheduled_role?.role_type);
    register(assignment?.scheduled_role_obj?.role_type);
    register(assignment?.scheduled_role_obj?.role_type?.role_type);
  });

  return Array.from(roleTypes);
}

function normalizePaidBreaks(paidBreaks) {
  if (!paidBreaks) {
    return [];
  }
  if (Array.isArray(paidBreaks)) {
    return paidBreaks;
  }
  if (typeof paidBreaks === "object") {
    return Object.values(paidBreaks);
  }
  return [];
}

export function parseActualScheduleResponse(data) {
  if (!data) {
    return [];
  }

  if (Array.isArray(data)) {
    return data.map((shift) => ({
      ...shift,
      paid_breaks: normalizePaidBreaks(shift?.paid_breaks),
    }));
  }

  const paidBreakLookup =
    data.redux?.payload?.paid_breaks || data?.paid_breaks || {};

  let schedule = [];

  if (data.redux?.payload?.actual_schedule_list) {
    schedule = data.redux.payload.actual_schedule_list;
  } else if (Array.isArray(data.actual_schedule_list)) {
    schedule = data.actual_schedule_list;
  }

  return schedule.map((shift) => {
    const paidBreaks = shift?.paid_breaks ?? paidBreakLookup?.[shift?.id];
    return {
      ...shift,
      paid_breaks: normalizePaidBreaks(paidBreaks),
    };
  });
}

export function parseActualUsedScheduleResponse(data) {
  if (!data) {
    return [];
  }
  if (Array.isArray(data)) {
    return data;
  }
  if (Array.isArray(data.redux?.payload)) {
    return data.redux.payload;
  }
  if (Array.isArray(data.actual_used_schedule)) {
    return data.actual_used_schedule;
  }
  return [];
}

export function parseStoreTeamMembersResponse(data) {
  if (!data) {
    return [];
  }
  if (Array.isArray(data)) {
    return data;
  }
  if (Array.isArray(data.redux?.payload?.store_team_members)) {
    return data.redux.payload.store_team_members;
  }
  if (Array.isArray(data.store_team_members)) {
    return data.store_team_members;
  }
  return [];
}

export function parseRoleTypesResponse(data) {
  if (!data) {
    return [];
  }
  if (Array.isArray(data)) {
    return data;
  }
  if (Array.isArray(data.redux?.payload?.role_types)) {
    return data.redux.payload.role_types;
  }
  if (Array.isArray(data.role_types)) {
    return data.role_types;
  }
  return [];
}

export function convertToQuarterIndex(value) {
  if (value === null || value === undefined) {
    return null;
  }
  const num = Number(value);
  if (Number.isNaN(num)) {
    return null;
  }
  if (num <= 48) {
    return Math.round(num * 4);
  }
  return Math.round(num);
}

export function getDayIndexFromDateString(dateString) {
  if (!dateString) {
    return null;
  }
  const match = /^\s*(\d{4})-(\d{2})-(\d{2})/.exec(dateString);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if ([year, month, day].some((value) => Number.isNaN(value))) {
    return null;
  }
  const utcDate = new Date(Date.UTC(year, month - 1, day));
  if (Number.isNaN(utcDate.getTime())) {
    return null;
  }
  const dayIndex = utcDate.getUTCDay();
  return ((dayIndex + 6) % 7);
}

export function useStableMemo(factory, deps) {
  // Lightweight helper to avoid eslint exhaustive-deps noise without adding the hook in every file.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(factory, deps);
}

export function safeLocaleCompare(valueA, valueB) {
  const stringA = valueA === null || valueA === undefined ? "" : String(valueA);
  const stringB = valueB === null || valueB === undefined ? "" : String(valueB);
  return stringA.localeCompare(stringB, undefined, { sensitivity: "base" });
}

const WEEKDAY_INDICES = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

export function parseDateLike(value) {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date;
}

export function getLookbackRange(historyRange, weeks) {
  const rawEnd = historyRange?.end || historyRange?.end_date || historyRange?.endDate;
  const endDate = parseDateLike(rawEnd) || new Date();
  const normalizedEnd = new Date(endDate.getTime());
  normalizedEnd.setHours(23, 59, 59, 999);

  const clampedWeeks = Math.max(Number.isFinite(Number(weeks)) ? Number(weeks) : 0, 1);
  const startDate = new Date(normalizedEnd.getTime());
  startDate.setDate(startDate.getDate() - clampedWeeks * 7 + 1);
  startDate.setHours(0, 0, 0, 0);

  const earliest = parseDateLike(historyRange?.start);
  if (earliest && startDate < earliest) {
    return { start: earliest, end: normalizedEnd };
  }

  return { start: startDate, end: normalizedEnd };
}

export function isDateWithinRange(value, range) {
  if (!range?.start || !range?.end) {
    return false;
  }
  const date = parseDateLike(value);
  if (!date) {
    return false;
  }
  return date >= range.start && date <= range.end;
}

export function getWeekStartDayIndex(dayName) {
  if (typeof dayName !== "string") {
    return WEEKDAY_INDICES.monday;
  }
  const normalized = dayName.trim().toLowerCase();
  return WEEKDAY_INDICES[normalized] ?? WEEKDAY_INDICES.monday;
}

export function getCompletedWeeksRange(weeks, weekStartIndex, referenceDate = new Date()) {
  const normalizedWeeks = Math.max(Number.isFinite(Number(weeks)) ? Number(weeks) : 0, 1);
  const startOfDay = new Date(referenceDate.getTime());
  startOfDay.setHours(0, 0, 0, 0);

  const normalizedWeekStart = Number.isFinite(Number(weekStartIndex))
    ? Number(weekStartIndex)
    : WEEKDAY_INDICES.monday;

  const daysSinceWeekStart =
    ((startOfDay.getDay() - normalizedWeekStart + 7) % 7);

  const currentWeekStart = new Date(startOfDay.getTime());
  currentWeekStart.setDate(currentWeekStart.getDate() - daysSinceWeekStart);
  currentWeekStart.setHours(0, 0, 0, 0);

  const lastCompletedWeekEnd = new Date(currentWeekStart.getTime());
  lastCompletedWeekEnd.setDate(currentWeekStart.getDate() - 1);
  lastCompletedWeekEnd.setHours(23, 59, 59, 999);

  const rangeEnd = lastCompletedWeekEnd;
  const rangeStart = new Date(rangeEnd.getTime());
  rangeStart.setDate(rangeStart.getDate() - (normalizedWeeks * 7 - 1));
  rangeStart.setHours(0, 0, 0, 0);

  return { start: rangeStart, end: rangeEnd };
}

function extractRoleTypeName(value) {
  if (!value) {
    return null;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (Array.isArray(value)) {
    for (let index = 0; index < value.length; index += 1) {
      const extracted = extractRoleTypeName(value[index]);
      if (extracted) {
        return extracted;
      }
    }
    return null;
  }
  if (typeof value === "object") {
    const candidates = [
      value.role_type,
      value.role_type_name,
      value.name,
      value.title,
      value.display_name,
      value.label,
    ];
    for (let index = 0; index < candidates.length; index += 1) {
      const extracted = extractRoleTypeName(candidates[index]);
      if (extracted) {
        return extracted;
      }
    }
  }
  return null;
}

export function buildRoleTypeNameMap(roleTypes) {
  const map = new Map();
  const registerName = (name) => {
    if (typeof name !== "string") {
      return null;
    }
    const trimmed = name.trim();
    if (!trimmed) {
      return null;
    }
    const normalized = normalizeRoleTypeString(trimmed);
    if (!map.has(normalized)) {
      map.set(normalized, normalized);
    }
    map.set(trimmed, normalized);
    return normalized;
  };

  const registerId = (id, name) => {
    if (id === null || id === undefined) {
      return;
    }
    const resolvedName = registerName(name);
    if (!resolvedName) {
      return;
    }
    map.set(id, resolvedName);
    map.set(String(id), resolvedName);
  };

  const collectFromValue = (value) => {
    if (value === null || value === undefined) {
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((item) => {
        collectFromValue(item);
      });
      return;
    }
    if (typeof value === "string") {
      registerName(value);
      return;
    }
    if (typeof value === "number") {
      return;
    }
    if (typeof value === "object") {
      const name =
        extractRoleTypeName(value) ||
        extractRoleTypeName(value.role_type) ||
        extractRoleTypeName(value.role_type_obj);
      if (name) {
        registerName(name);
        const ids = [
          value.id,
          value.role_type_id,
          value.role_type?.id,
          value.role_type_obj?.id,
        ];
        ids.forEach((id) => registerId(id, name));
      }
      const nestedValues = [
        value.role_type,
        value.role_type_obj,
        value.role_type_info,
        value.assigned_role_type,
      ];
      nestedValues.forEach((nested) => collectFromValue(nested));
    }
  };

  if (roleTypes !== null && roleTypes !== undefined) {
    collectFromValue(roleTypes);
  }

  return map;
}

export function resolveRoleTypeName(rawValue, roleTypeNameMap) {
  const map = roleTypeNameMap || new Map();
  if (rawValue === null || rawValue === undefined) {
    return null;
  }
  if (typeof rawValue === "string") {
    const trimmed = rawValue.trim();
    if (!trimmed) {
      return null;
    }
    const normalized = normalizeRoleTypeString(trimmed);
    const lookup = map.get(normalized) || map.get(trimmed);
    if (lookup) {
      return lookup;
    }
    return normalized;
  }
  if (typeof rawValue === "number") {
    const lookup = map.get(rawValue) || map.get(String(rawValue));
    if (lookup) {
      return lookup;
    }
    return String(rawValue);
  }
  if (Array.isArray(rawValue)) {
    for (let index = 0; index < rawValue.length; index += 1) {
      const resolved = resolveRoleTypeName(rawValue[index], map);
      if (resolved) {
        return resolved;
      }
    }
    return null;
  }
  if (typeof rawValue === "object") {
    const candidates = [
      rawValue.role_type,
      rawValue.role_type_name,
      rawValue.name,
      rawValue.title,
      rawValue.display_name,
      rawValue.label,
    ];
    for (let index = 0; index < candidates.length; index += 1) {
      const resolved = resolveRoleTypeName(candidates[index], map);
      if (resolved) {
        return resolved;
      }
    }
    if (rawValue.id !== null && rawValue.id !== undefined) {
      const resolved = resolveRoleTypeName(rawValue.id, map);
      if (resolved) {
        return resolved;
      }
    }
    if (
      rawValue.role_type_id !== null &&
      rawValue.role_type_id !== undefined
    ) {
      const resolved = resolveRoleTypeName(rawValue.role_type_id, map);
      if (resolved) {
        return resolved;
      }
    }
    return null;
  }
  return null;
}

export function registerRoleTypeName(rawValue, roleTypeNameMap) {
  const map = roleTypeNameMap || new Map();
  const resolved = resolveRoleTypeName(rawValue, map);
  if (!resolved) {
    return resolved;
  }
  map.set(resolved, resolved);
  if (rawValue && typeof rawValue === "object") {
    const ids = [
      rawValue.id,
      rawValue.role_type_id,
      rawValue.role_type?.id,
      rawValue.role_type_obj?.id,
    ];
    ids.forEach((id) => {
      if (id !== null && id !== undefined) {
        map.set(id, resolved);
        map.set(String(id), resolved);
      }
    });
  } else if (typeof rawValue === "number") {
    map.set(rawValue, resolved);
    map.set(String(rawValue), resolved);
  } else if (typeof rawValue === "string") {
    const trimmed = rawValue.trim();
    if (trimmed) {
      const normalized = normalizeRoleTypeString(trimmed);
      map.set(trimmed, resolved);
      map.set(normalized, resolved);
    }
  }
  return resolved;
}
